/**
 * Best-effort copy to the clipboard using a legacy hidden-textarea +
 * `document.execCommand('copy')`. The textarea is off-screen, so the value is
 * never visible to the user. Returns whether the copy succeeded.
 */
const execCommandCopy = (text: string): boolean => {
    if (typeof document === 'undefined') return false;

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);

    const selection = document.getSelection();
    const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    textarea.select();

    let succeeded = false;
    try {
        succeeded = document.execCommand('copy');
    } catch {
        succeeded = false;
    }

    document.body.removeChild(textarea);

    // Restore any selection the user had before we hijacked it.
    if (previousRange && selection) {
        selection.removeAllRanges();
        selection.addRange(previousRange);
    }

    return succeeded;
};

/**
 * Copies the given text to the clipboard.
 *
 * Prefers the async Clipboard API; if it is unavailable or blocked/denied,
 * falls back to a hidden-textarea `execCommand` copy so the action doesn't just
 * fail with no plan B. Rejects only when both approaches fail. The value is
 * never rendered on screen or logged.
 */
export const copyToClipboard = async (text: string): Promise<void> => {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } catch {
            // Clipboard API blocked/denied — fall through to the legacy fallback.
        }
    }

    if (!execCommandCopy(text)) {
        throw new Error('Unable to copy to clipboard');
    }
};

export default copyToClipboard;
